import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  delay, map, reduce, indexOf,
  concat, find, reject as lodashReject,
} from 'lodash';
import Fa from 'react-fontawesome';
import Joi from 'joi';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import Autosuggest from 'react-autosuggest';
import Avatar from '../../../../components/avatar';
import styles from '../../../Team/components/ShareModal/styles.css';
import { fetchOrganizationUsers } from '../../SetupActions';

function escapeRegexCharacters(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
function blurAll(parent = document.body) {
  const tmp = document.createElement('input');
  tmp.className = styles.hiddenInput;
  parent.appendChild(tmp);
  delay(() => {
    tmp.focus();
    parent.removeChild(tmp);
  }, 1);
}
const FadeAnimation = ({ children, ...props }) => (
  <CSSTransition
    {...props}
  >
    {children}
  </CSSTransition>
);
FadeAnimation.propTypes = {
  children: PropTypes.element,
};
FadeAnimation.defaultProps = {
  children: null,
};


class RecipientAutocomplete extends Component {
  constructor(props) {
    super(props);
    this.state = {
      value: '',
      loading: false,
      focus: false,
      suggestions: [],
      success: false,
    };
    this.onChange = this.onChange.bind(this);
    this.onFocus = this.onFocus.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.onSuggestionsFetchRequested = this.onSuggestionsFetchRequested.bind(this);
    this.onSuggestionsClearRequested = this.onSuggestionsClearRequested.bind(this);
    this.onSuggestionSelected = this.onSuggestionSelected.bind(this);
    this.getSuggestionValue = this.getSuggestionValue.bind(this);
    this.getSectionSuggestions = this.getSectionSuggestions.bind(this);
    this.removeTag = this.removeTag.bind(this);
    this.renderSuggestion = this.renderSuggestion.bind(this);
  }

  onChange(event, { newValue }) {
    this.setState({
      value: newValue,
    });
  }

  onFocus() {
    this.setState({ focus: true });
  }

  onBlur() {
    this.setState({ focus: false });
  }

  // Autosuggest will call this function every time
  // you need to update suggestions.
  onSuggestionsFetchRequested({ value }) {
    const val = (value || '').trim();

    if (val && val.length > 0) {
      const escapedValue = escapeRegexCharacters(val);
      this.setState({ loading: true, suggestions: [] });
      const existentIds = map(this.props.recipients, '_id');

      const p = new Promise((resolve, reject) => {
        this.props.dispatch(fetchOrganizationUsers(this.props.organization._id, { q: escapedValue }))
          .then(
            ({ users }) => {
              const validUsers = reduce(users.items, (items, user) => {
                if (indexOf(existentIds, user._id) < 0) {
                  items.push(user);
                }
                return items;
              }, []);
              if (!Joi.string().email({ tlds: { allow: false } }).validate(val).error) {
                const existent = find(validUsers, { _id: val });
                if (!existent) {
                  validUsers.push({
                    _id: val,
                    email: val,
                    isEmail: true,
                  });
                }
              }
              resolve(validUsers);
            },
            (errors) => {
              reject(errors);
            },
          );
      });
      p.then(
        (suggestions) => {
          this.setState({ suggestions, loading: false });
        },
        () => {
          this.setState({ suggestions: [], loading: false });
        },
      );
      return p;
    }
    return Promise.resolve([]);
  }
  // Autosuggest will call this function every time you need to clear suggestions.
  onSuggestionsClearRequested() {
    this.setState({ suggestions: [] });
  }

  onSuggestionSelected(event, { suggestion }) {
    const { recipients } = this.props;
    const exist = find(recipients, { _id: suggestion._id });
    if (!exist) {
      const newValues = concat(recipients, suggestion);
      this.props.onChange(newValues);
    }
    this.setState({ value: '' });
    blurAll(this.element);
  }

  getSuggestionValue(suggestion) {
    return suggestion.label;
  }

  getSectionSuggestions(section) {
    return section.suggestions;
  }

  removeTag(suggestion) {
    const { recipients } = this.props;
    const newValues = lodashReject(recipients, val => val._id === suggestion._id);
    this.props.onChange(newValues);
  }

  renderSuggestion(suggestion, { query }) {
    const label = suggestion.isEmail ? suggestion.email : (suggestion.display_name || suggestion.nick_name || suggestion.username);
    const matches = match(label, query);
    const parts = parse(label, matches).map((part, id) => ({ ...part, id }));

    return (
      <a href="" onClick={(e) => { e.preventDefault(); }}>
        {!suggestion.isEmail && <Avatar small user={suggestion} />}
        {suggestion.isEmail && <span><Fa name="envelope-o" />&nbsp;&nbsp;</span>}
        <span>
          {parts.map((part) => {
            const className = part.highlight ? styles.tagHighlight : null;
            return (
              <span className={className} key={part.id}>
                {part.text}
              </span>
            );
          })}
        </span>
      </a>
    );
  }

  render() {
    const {
      value, suggestions, focus,
      loading,
    } = this.state;
    const { submitting, recipients } = this.props;

    const inputProps = {
      placeholder: 'type a user name or email...',
      value,
      className: styles.tagInput,
      onChange: this.onChange,
      onFocus: this.onFocus,
      onBlur: this.onBlur,
      disabled: submitting,
    };
    const theme = {
      container: styles.autocomplete,
      suggestionsContainer: `dropdown open ${styles.suggestionsContainer}`,
      sectionContainer: `dropdown-menu ${styles.sectionContainer}`,
      suggestionsList: `dropdown-menu ${styles.suggestionsList}`,
      sectionTitle: 'dropdown-header',
      suggestion: '',
      suggestionFocused: 'active',
    };

    const cls = [styles.tagInputField];
    if (focus) {
      cls.push(styles.tagInputFieldFocus);
    }
    if (loading) {
      cls.push(styles.tagInputFieldLoading);
    }

    return (
      <div
        ref={(el) => { this.element = el; }}
        className={`${cls.join(' ')}`}
      >
        <TransitionGroup className={styles.tagList}>
          {recipients.map((tag) => {
            const tCls = [styles.tag];
            tCls.push(styles.valueTag);
            if (!tag.isEmail) {
              tCls.push(styles.userTag);
            }
            if (submitting) {
              tCls.push(styles.disabledTag);
            }
            const label = tag.isEmail ? tag.email : (tag.display_name || tag.nick_name || tag.username);
            return (
              <FadeAnimation
                classNames="tag"
                timeout={500}
                key={tag._id}
              >
                <span className={`${tCls.join(' ')}`}>
                  {!tag.isEmail && <Avatar small user={tag} />}
                  {tag.isEmail && <span><Fa name="envelope-o" />&nbsp;</span>}
                  <span className={styles.tagLabel}>{label}</span>
                  <a
                    href=""
                    className={styles.tagTrash}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!submitting) {
                        this.removeTag(tag);
                      }
                    }}
                  >
                    <Fa name="times" />
                  </a>
                </span>
              </FadeAnimation>
            );
          })}
          <FadeAnimation
            classNames="tag"
            timeout={500}
          >
            <div className={styles.autocompleteWrapper}>
              <Autosuggest
                theme={theme}
                suggestions={suggestions}
                getSectionSuggestions={this.getSectionSuggestions}
                onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
                shouldRenderSuggestions={() => true}
                onSuggestionsClearRequested={this.onSuggestionsClearRequested}
                onSuggestionSelected={this.onSuggestionSelected}
                getSuggestionValue={this.getSuggestionValue}
                renderSuggestion={this.renderSuggestion}
                inputProps={inputProps}
              />
              <Fa name="spinner" spin className={styles.spinner} />
            </div>
          </FadeAnimation>
        </TransitionGroup>
      </div>
    );
  }
}

function mapStateToProps() {
  return {};
}

RecipientAutocomplete.propTypes = {
  recipients: PropTypes.arrayOf(PropTypes.shape({
    _id: PropTypes.string,
  })).isRequired,
  dispatch: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
  onChange: PropTypes.func,
  organization: PropTypes.shape({
    _id: PropTypes.string.isRequired,
  }).isRequired,
};
RecipientAutocomplete.defaultProps = {
  submitting: false,
  onChange: () => {},
};


export default connect(mapStateToProps)(RecipientAutocomplete);
